import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';

import Main from 'layouts/Main';
import SEO from 'components/SEO';
import { CallToAction } from 'components/CallToAction';
import ShareButton from '../shared/ShareButton';
import { getArchetype, archetypes } from './resultContent';

/**
 * Shareable quiz result page
 * Loaded via /quiz/bettor-type/result/:archetype
 * Shows the archetype result without requiring quiz completion
 */
const BettorTypeResult = () => {
  const { archetype: archetypeSlug } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  // Convert slug (e.g. "edge-seeker") to key (e.g. "edge_seeker")
  const archetypeKey = archetypeSlug?.replace(/-/g, '_');
  const archetype = archetypes[archetypeKey];

  // Invalid archetype — redirect to quiz
  if (!archetype) {
    return (
      <Main bgcolor="background.paper">
        <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Result not found
          </Typography>
          <Button variant="contained" onClick={() => navigate('/quiz/bettor-type')}>
            Take the Quiz
          </Button>
        </Container>
      </Main>
    );
  }

  return (
    <Main bgcolor="background.paper">
      <SEO
        title={`${archetype.displayName} — Bettor Quiz Result | +EV Bets`}
        description={archetype.description}
      />

      {/* Result Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${archetype.color} 0%, ${archetype.color}dd 100%)`,
          color: 'white',
          py: { xs: 5, md: 7 },
          textAlign: 'center'
        }}
      >
        <Container maxWidth="sm">
          <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 2 }}>
            Bettor Quiz Result
          </Typography>
          <Typography
            variant="h3"
            sx={{ fontWeight: 800, fontSize: { xs: '2rem', sm: '2.5rem' }, mb: 1 }}
          >
            {archetype.displayName}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
            {archetype.tagline}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography
          variant="body1"
          sx={{ fontSize: '1.1rem', lineHeight: 1.7, mb: 4, textAlign: 'center' }}
        >
          {archetype.description}
        </Typography>

        {/* Strengths */}
        <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
              Strengths
            </Typography>
            {archetype.strengths.map((strength, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Typography variant="body2">{strength}</Typography>
              </Box>
            ))}
          </CardContent>
        </Card>

        <Divider sx={{ my: 4 }} />

        {/* Brand hook + CTA */}
        <Typography
          variant="body2"
          sx={{ textAlign: 'center', color: 'text.secondary', fontStyle: 'italic', mb: 3 }}
        >
          FairLine™ does the math. EdgeBoard™ does the work.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
          <CallToAction
            type="trial"
            src="quiz-result-share"
            sx={{
              minHeight: 56,
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 2
            }}
          />
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/quiz/bettor-type')}
            sx={{ minHeight: 56, fontWeight: 600, borderRadius: 2 }}
          >
            Take the Quiz Yourself
          </Button>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Share this result
          </Typography>
          <ShareButton
            shareText={archetype.shareText}
            shareUrl={`${window.location.origin}/quiz/bettor-type/result/${archetypeSlug}`}
            onShare={() => {}}
          />
        </Box>
      </Container>
    </Main>
  );
};

export default BettorTypeResult;
